import { useState } from "react";
import { motion } from "framer-motion";
import { FileText, Download, Eye, ChevronDown, ChevronUp, Wrench, Package, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InstructionsPage = () => {
  const [showDetails, setShowDetails] = useState(false);

  const warrantyInfo = [
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Гарантия 12 месяцев",
      description: "На производственные дефекты с момента покупки",
    },
    {
      icon: <Package className="w-5 h-5" />,
      title: "Комплектация",
      description: "Полки, боковые панели, крепеж для стены",
    },
    {
      icon: <Wrench className="w-5 h-5" />,
      title: "Простая сборка",
      description: "Не требует специальных инструментов",
    },
  ];

  const careInstructions = [
    "Температура эксплуатации: от +10°C до +40°C",
    "Относительная влажность: 45-70%",
    "Протирать мягкой тканью без абразивных средств",
    "Не допускать попадания влаги на поверхность",
    "Располагать на расстоянии не менее 50 см от источников тепла",
    "Избегать длительного воздействия прямых солнечных лучей",
  ];

  return (
    <div className="section-padding" data-testid="instructions-page">
      <SEO
        title="Инструкция по сборке полки BE KIDS — Скачать PDF для моделей L, XL, XXL"
        description="Подробная инструкция по сборке детской настенной полки BE KIDS. Схема сборки одинакова для моделей L, XL и XXL. Скачайте PDF с гарантийными условиями."
        path="/instructions"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-full bg-[#E6E6FA] flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-[#1A1A1A]" />
          </div>
          <h1 className="heading-1 mb-4" data-testid="instructions-title">
            Инструкция по сборке
          </h1>
          <p className="body-text max-w-xl mx-auto" data-testid="instructions-description">
            Универсальная инструкция для настенных полок BE KIDS всех размеров (L, XL, XXL)
          </p>
        </motion.div>

        {/* PDF Download Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-soft p-8 md:p-12 bg-[#E6E6FA]/20 mb-8"
          data-testid="pdf-section"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-32 h-40 bg-white rounded-2xl shadow-lg flex items-center justify-center border border-[#1A1A1A]/10">
              <div className="text-center">
                <FileText className="w-12 h-12 text-[#ADD8E6] mx-auto mb-2" />
                <span className="text-xs font-bold text-[#808080]">PDF</span>
              </div>
            </div>

            <div className="flex-1 text-center md:text-left">
              <h2 className="heading-2 mb-3" data-testid="pdf-title">
                BE KIDS — Инструкция
              </h2>
              <p className="body-text text-sm mb-6">
                Скачайте PDF-файл с подробной инструкцией по сборке и уходу за изделием. 
                Инструкция подходит для всех моделей настенных полок BE KIDS.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <a
                  href={`${BACKEND_URL}/api/instructions/pdf?v=3`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pdf-view-btn"
                >
                  <Button className="btn-primary flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Открыть PDF
                  </Button>
                </a>
                <a
                  href={`${BACKEND_URL}/api/instructions/pdf?v=3`}
                  download="BE_KIDS_Instructions.pdf"
                  data-testid="pdf-download-btn"
                >
                  <Button className="btn-secondary flex items-center gap-2">
                    <Download className="w-4 h-4" />
                    Скачать
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Warranty Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8"
        >
          {warrantyInfo.map((item, index) => (
            <div
              key={index}
              className="card-soft p-6 text-center"
              data-testid={`warranty-info-${index}`}
            >
              <div className="w-12 h-12 rounded-full bg-[#98FF98]/50 flex items-center justify-center mx-auto mb-4">
                {item.icon}
              </div>
              <h3 className="heading-3 text-base mb-2">{item.title}</h3>
              <p className="text-sm text-[#4A4A4A]">{item.description}</p>
            </div>
          ))}
        </motion.div>

        {/* Care Instructions Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-soft overflow-hidden"
          data-testid="care-section"
        >
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="w-full p-6 flex items-center justify-between hover:bg-[#FCFAF8] transition-colors"
            data-testid="care-toggle-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#FFFACD] flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <span className="heading-3 text-lg">Рекомендации по эксплуатации и уходу</span>
            </div>
            {showDetails ? (
              <ChevronUp className="w-5 h-5 text-[#808080]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#808080]" />
            )}
          </button>

          {showDetails && (
            <div className="px-6 pb-6" data-testid="care-details">
              <ul className="space-y-3 ml-14">
                {careInstructions.map((instruction, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-[#4A4A4A]"
                    data-testid={`care-instruction-${index}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#ADD8E6] mt-2 flex-shrink-0" />
                    {instruction}
                  </li>
                ))}
              </ul>

              <div className="mt-6 p-4 bg-[#FFDAB9]/20 rounded-xl ml-14">
                <p className="text-sm text-[#4A4A4A]">
                  <strong className="text-[#1A1A1A]">Важно:</strong> Гарантия не распространяется 
                  на повреждения, вызванные несоблюдением условий эксплуатации, механическими 
                  воздействиями или неправильной сборкой.
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Assembly Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 card-soft p-8 bg-[#ADD8E6]/10"
          data-testid="assembly-tips"
        >
          <h3 className="heading-3 mb-6">Советы по сборке</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#ADD8E6] flex items-center justify-center flex-shrink-0 text-[#1A1A1A] font-bold">
                1
              </div>
              <div>
                <h4 className="font-semibold text-[#1A1A1A] mb-1">Подготовьте место</h4>
                <p className="text-sm text-[#4A4A4A]">
                  Выберите ровную стену на уровне глаз ребенка. Убедитесь, что рядом нет источников тепла.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#ADD8E6] flex items-center justify-center flex-shrink-0 text-[#1A1A1A] font-bold">
                2
              </div>
              <div>
                <h4 className="font-semibold text-[#1A1A1A] mb-1">Проверьте комплектацию</h4>
                <p className="text-sm text-[#4A4A4A]">
                  Перед сборкой убедитесь, что все детали на месте и не имеют видимых повреждений.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#ADD8E6] flex items-center justify-center flex-shrink-0 text-[#1A1A1A] font-bold">
                3
              </div>
              <div>
                <h4 className="font-semibold text-[#1A1A1A] mb-1">Используйте уровень</h4>
                <p className="text-sm text-[#4A4A4A]">
                  При креплении к стене используйте строительный уровень для ровной установки.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 rounded-full bg-[#ADD8E6] flex items-center justify-center flex-shrink-0 text-[#1A1A1A] font-bold">
                4
              </div>
              <div>
                <h4 className="font-semibold text-[#1A1A1A] mb-1">Надежное крепление</h4>
                <p className="text-sm text-[#4A4A4A]">
                  Используйте дюбели, подходящие для вашего типа стены. При сомнениях обратитесь к специалисту.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InstructionsPage;
